import { Fragment, useEffect, useRef, useState } from "react";
import "./App.css";
import logo from "./logo.png";
import LoginPage from "./LoginPage";
import Toast from "./components/Toast";
import BellMenu from "./components/BellMenu";
import UploadTab from "./components/UploadTab";
import ProcessingTab from "./components/ProcessingTab";
import ProcessedTab from "./components/ProcessedTab";
import MapTab from "./components/MapTab";
import usePersistedState from "./hooks/usePersistedState";
import useToast from "./hooks/useToast";
import useSocketRefresh from "./hooks/useSocketRefresh";
import useProcessCount from "./hooks/useProcessCount";
import useProcessedHistory from "./hooks/useProcessedHistory";
import useVideoList from "./hooks/useVideoList";
import useNotifications from "./hooks/useNotifications";
import useVideoMetadata from "./hooks/useVideoMetadata";
import { clearCurrentUser, isLoggedIn } from "./auth";
import { registerMissingVideos } from "./api";
import { disconnectSocket } from "./socket";

const currentMonthKey = (now = new Date()) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

const TABS = [
  { index: 0, label: "Upload Videos" },
  { index: 1, label: "Video Processing Details" },
  { index: 2, label: "Video Processed Details" },
  { index: 3, label: "Map View" },
];

function App() {
  const [loggedIn, setLoggedIn] = useState(() => isLoggedIn());
  const [tabIndex, setTabIndex] = usePersistedState("selectedTab", 0, {
    parse: Number,
  });
  const [processedDate, setProcessedDate] = usePersistedState(
    "selectedProcessedDate",
    null,
  );
  const [historyDate, setHistoryDate] = usePersistedState("selectedDate", null);

  const { toast, showToast } = useToast();
  const { progress, remainingMinutes, videos, lastRefreshAt } = useSocketRefresh(loggedIn);
  const processCount = useProcessCount(loggedIn && tabIndex === 1, processedDate, lastRefreshAt);
  const dayList = useVideoList(loggedIn && tabIndex === 1, processedDate, lastRefreshAt);
  const history = useProcessedHistory(loggedIn && tabIndex === 2, historyDate);
  // The Map View reads one month at a time: the current month by recording
  // date until the calendar says otherwise.
  const [mapQuery, setMapQuery] = useState(() => ({ month: currentMonthKey(), by: "video" }));
  const metadata = useVideoMetadata(loggedIn && tabIndex === 3, mapQuery);
  const notifications = useNotifications(loggedIn);

  // When a batch starts (the list goes from empty to non-empty), jump to the
  // processing tab. Later refreshes of a running batch leave the tab alone.
  const hadVideos = useRef(false);
  useEffect(() => {
    if (videos.length > 0 && !hadVideos.current) setTabIndex(1);
    hadVideos.current = videos.length > 0;
  }, [videos, setTabIndex]);

  const handleLogout = () => {
    disconnectSocket();
    clearCurrentUser();
    setTabIndex(0);
    setLoggedIn(false);
  };

  if (!loggedIn) {
    return <LoginPage onLogin={() => setLoggedIn(true)} />;
  }

  return (
    <div className="App">
      <div className="AppHeader">
        <div className="left-section">
          <img src={logo} className="App-logo" alt="logo" />
        </div>
        <div className="right-section">
          <BellMenu
            items={notifications.items}
            unread={notifications.unread}
            status={notifications.status}
            error={notifications.error}
            onOpen={notifications.markAllRead}
          />
          <div className="logout-btn" onClick={handleLogout}>
            Logout
          </div>
        </div>
      </div>

      <div className="tab-container">
        <div className="tab-buttons">
          {TABS.map((tab, i) => (
            <Fragment key={tab.index}>
              {i > 0 && <hr />}
              <button
                className={tabIndex === tab.index ? "tabs selected-tab" : "tabs"}
                onClick={() => setTabIndex(tab.index)}
              >
                {tab.label}
              </button>
            </Fragment>
          ))}
        </div>
      </div>

      {tabIndex === 0 && <UploadTab showToast={showToast} />}

      {tabIndex === 1 && (
        <ProcessingTab
          progress={progress}
          remainingMinutes={remainingMinutes}
          videos={videos}
          counts={processCount.counts}
          countStatus={processCount.status}
          countError={processCount.error}
          dayList={dayList}
          selectedDate={processedDate}
          onDateChange={setProcessedDate}
          onGoToUpload={() => setTabIndex(0)}
          showToast={showToast}
        />
      )}

      {tabIndex === 2 && (
        <ProcessedTab
          blocks={history.blocks}
          status={history.status}
          error={history.error}
          selectedDate={historyDate}
          onDateChange={setHistoryDate}
        />
      )}

      {tabIndex === 3 && (
        <MapTab
          data={metadata.data}
          status={metadata.status}
          error={metadata.error}
          onReload={metadata.reload}
          registerMissing={registerMissingVideos}
          month={mapQuery.month}
          mode={mapQuery.by}
          onMonthChange={(month) => setMapQuery((q) => ({ ...q, month }))}
          onModeChange={(by) => setMapQuery((q) => ({ ...q, by }))}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default App;
